// AIT-268 — single source of the marketing copy that names a channel.
//
// `/` renders the 'both' variant, which is the wording the site shipped with;
// `/whatsapp` and `/instagram` narrow it to one channel. Anything added here
// must follow the copy rules in CLAUDE.md: Title Case for labels, no em
// dashes, and no internal mechanics (no Meta scopes, no permission plumbing).
export type Channel = 'whatsapp' | 'instagram' | 'both';

export interface ChannelCopy {
  heroHeadline: string;
  finalCtaHeadline: string;
  builtForHeadline: string;
  builtForIntro: string;
  vibeCoders: string;
  developers: string;
  influencers: string;
  agencies: string;
}

export const CHANNEL_COPY: Record<Channel, ChannelCopy> = {
  both: {
    heroHeadline: 'AI access to WhatsApp and Instagram',
    finalCtaHeadline: 'Go live on WhatsApp or Instagram in 2 minutes for free.',
    builtForHeadline: 'Built to go live on WhatsApp and Instagram now',
    builtForIntro:
      'HookMyApp is for vibe coders, developers, influencers, and agencies who want both channels running without weeks of setup hell.',
    vibeCoders: 'One prompt. Then your app can post, reply, and message on its own.',
    developers:
      'Paste one prompt. Skip weeks of Meta setup that has nothing to do with your product.',
    influencers:
      'Social autopilot. Reels and stories go out. Comments and DMs get replies.',
    agencies:
      "Get access to a client's WhatsApp and Instagram in minutes. Act on their behalf.",
  },
  // Single-channel variants never name the other channel, and never promise a
  // capability that channel does not have: WhatsApp has no comments, reels, or
  // stories, so those words appear only in the Instagram and both variants.
  whatsapp: {
    heroHeadline: 'AI access to WhatsApp',
    finalCtaHeadline: 'Go live on WhatsApp in 2 minutes for free.',
    builtForHeadline: 'Built to go live on WhatsApp now',
    builtForIntro:
      'HookMyApp is for vibe coders, developers, influencers, and agencies who want WhatsApp running without weeks of setup hell.',
    vibeCoders: 'One prompt. Then your app can reply and message on its own.',
    developers:
      'Paste one prompt. Skip weeks of Meta setup that has nothing to do with your product.',
    influencers: 'Social autopilot. DMs get replies without you living in the inbox.',
    agencies:
      "Get access to a client's WhatsApp in minutes. Act on their behalf.",
  },
  instagram: {
    heroHeadline: 'AI access to Instagram',
    finalCtaHeadline: 'Go live on Instagram in 2 minutes for free.',
    builtForHeadline: 'Built to go live on Instagram now',
    builtForIntro:
      'HookMyApp is for vibe coders, developers, influencers, and agencies who want Instagram running without weeks of setup hell.',
    vibeCoders: 'One prompt. Then your app can post, reply, and message on its own.',
    developers:
      'Paste one prompt. Skip weeks of Meta setup that has nothing to do with your product.',
    influencers:
      'Social autopilot. Reels and stories go out. Comments and DMs get replies.',
    agencies:
      "Get access to a client's Instagram in minutes. Act on their behalf.",
  },
};

export const CHANNEL_LABEL: Record<'whatsapp' | 'instagram', string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
};
